import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil';
import { onsaProgressAtom } from '../../recoil/atoms/onsaProgressAtom';
import OnsaProgressBar from '../../shared_components/OnsaProgressBar/OnsaProgressBar';
import type { WizardPageComponent } from '../../config/wizardPagesConfig';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import { getOnsaCurrentProductSelector } from '../../recoil/selectors/getOnsaCurrentProductSelector';
import { getClaimsHistoryConfig } from './ClaimsHistoryStepsConfig';
import { allCoverablesOnsaAtom } from '../../recoil/atoms/allCoverablesOnsaAtom';
import { getCoverableInformationConfig } from '../CoverableInformationPage/CoverableInformationStepsConfig';
import { Grid, GridItem, Wizard } from '@flos/react-ui';
import { getOnsaProgressSelector } from '../../recoil/selectors/getOnsaProgressSelector';
import { claimsHistorySelector } from '../../recoil/selectors/claimsHistorySelector';

const ClaimsHistoryPage: WizardPageComponent = () => {
    const { wizardGotoStep } = useWizardNavigation();
    const [onsaProgress, setOnsaProgress] = useRecoilState(onsaProgressAtom);
    const onsaNavigation = useRecoilValue(getOnsaProgressSelector);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const coverableSteps = getCoverableInformationConfig(currentProduct, coverables).steps.filter(
        (step) => !step.disabled
    );
    const shouldSendClaimsHistory = useRecoilValue(claimsHistorySelector);
    const claimsSteps = shouldSendClaimsHistory
        ? getClaimsHistoryConfig(currentProduct, coverables).filter((step) => !step.disabled)
        : [];
    const currentStep = onsaProgress.currentStep - coverableSteps.length;

    const goNext = useRecoilCallback(
        ({ snapshot }) =>
            () => {
                void snapshot.getPromise(allCoverablesOnsaAtom).then((allCoverables) => {
                    const claimsSteps = shouldSendClaimsHistory
                        ? getClaimsHistoryConfig(currentProduct, allCoverables).filter(
                              (step) => !step.disabled
                          )
                        : [];
                    if (currentStep === claimsSteps.length) {
                        wizardGotoStep(onsaNavigation.nextStep);
                    }
                    setOnsaProgress((prevStep) => ({
                        ...prevStep,
                        currentStep: prevStep.currentStep + 1
                    }));
                });
            },
        [allCoverablesOnsaAtom, currentStep, currentProduct]
    );

    const goPrevious = (): void => {
        if (currentStep === 1) {
            wizardGotoStep(onsaNavigation.prevStep);
        }
        setOnsaProgress((prevStep) => ({
            ...prevStep,
            currentStep: prevStep.currentStep - 1
        }));
    };

    if (!currentProduct || claimsSteps.length === 0) {
        return <></>;
    }

    return (
        <Wizard.Container currentStep={currentStep}>
            {claimsSteps.map((step) => {
                const { id, Component } = step;
                return (
                    <Wizard.Pane key={id} hideProgress>
                        <Grid>
                            <GridItem span={8} colStart={3}>
                                <OnsaProgressBar />
                                <Component goNext={goNext} goPrevious={goPrevious} />
                            </GridItem>
                        </Grid>
                    </Wizard.Pane>
                );
            })}
        </Wizard.Container>
    );
};

export default ClaimsHistoryPage;
